import React, { useState } from "react";
import { Alert, Anchor, Button, Group, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { Employee } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import { SettingsTabs } from "../common";
import { splitCommissionHistory } from "./commissionHistory";
import { formatEffectiveDate, staffName } from "./commissionDisplay";
import CommissionDrawerView from "./CommissionDrawerView";
import CurrentRateDetails from "./CurrentRateDetails";
import PastRatesList from "./PastRatesList";

export interface RateDetailsViewProps {
  employee: Employee;
  records: CommissionRecord[];
  canEdit: boolean;
  isMobile: boolean;
  onEdit: (record: CommissionRecord) => void;
  onAddNew: () => void;
  onDelete: (record: CommissionRecord) => void;
}

type DetailsTab = "current" | "past";

/** Banner for a rate that starts in the future. */
const UpcomingRate: React.FC<{
  record: CommissionRecord;
  onEdit?: () => void;
}> = ({ record, onEdit }) => (
  <Alert variant="light" color="blue" p="xs">
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm">
        A new rate starts on {formatEffectiveDate(record.effective_start_date)}.
      </Text>
      {onEdit && (
        <Anchor component="button" type="button" size="sm" onClick={onEdit}>
          Edit
        </Anchor>
      )}
    </Group>
  </Alert>
);

/** The drawer's first screen: the rate in force and the rates before it. */
const RateDetailsView: React.FC<RateDetailsViewProps> = ({
  employee,
  records,
  canEdit,
  isMobile,
  onEdit,
  onAddNew,
  onDelete,
}) => {
  const [tab, setTab] = useState<DetailsTab>("current");
  const { current, upcoming, past } = splitCommissionHistory(records);
  return (
    <CommissionDrawerView
      title={`Commission Rates for ${staffName(employee)}`}
      actions={
        canEdit && (
          <Button
            color="teal"
            size={isMobile ? "xs" : "sm"}
            leftSection={<IconPlus size={16} />}
            onClick={onAddNew}
          >
            {isMobile ? "Add Rate" : "Add New Rate"}
          </Button>
        )
      }
      tabs={
        <SettingsTabs
          ariaLabel="Commission rates"
          value={tab}
          onChange={(value) => setTab(value as DetailsTab)}
          items={[
            { value: "current", label: "Current" },
            { value: "past", label: `Past rates · ${past.length}` },
          ]}
        />
      }
    >
      {tab === "current" ? (
        <Stack gap="md">
          {upcoming.map((record) => (
            <UpcomingRate
              key={record.id}
              record={record}
              onEdit={canEdit ? () => onEdit(record) : undefined}
            />
          ))}
          {current ? (
            <CurrentRateDetails
              commission={current}
              staffId={employee.id}
              onEdit={canEdit ? () => onEdit(current) : undefined}
            />
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No commission structure set.
            </Text>
          )}
        </Stack>
      ) : (
        <PastRatesList
          rates={past}
          staffId={employee.id}
          canEdit={canEdit}
          compact={isMobile}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </CommissionDrawerView>
  );
};

export default RateDetailsView;
